import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AgGridReact } from 'ag-grid-react';
import useFetchNetsuiteDropdown from '../../../hooks/useFetchNetsuiteDropdown';
import useFetchProjectsProcurement from '../../../hooks/useFetchProjectsProcurement';
import useFetchVendorsProcurement from '../../../hooks/useFetchVendorsProcurement';
import DenyReasonDialog from '../../UserDialog/DenyReasonDialog';
import ConfirmDialog from '../../UserDialog/ConfirmDialog';
import PoGridExpansionModal from './PoGridExpansionModal';
import { useNetSuiteProductionRfq } from '../../../config/netsuite';
import * as XLSX from 'xlsx';

import {
  ClientSideRowModelModule,
  ClipboardModule,
  RichSelectModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  RichSelectModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
]);

const normalizedFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) => {
    if (option.name && option.afeNumber !== undefined) {
      // This is a project
      return `${option.name}${option.afeNumber}`.replace(/[\s\u00A0]+/g, '').toLowerCase();
    }
    if (option.companyName) {
      // This is a vendor
      return `${option.companyName}`.replace(/[\s\u00A0]+/g, '').toLowerCase();
    }
    return '';
  },
});

const toFixedNumber = (value, precision) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 0;
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
};

const PoDataEntryModal = (props) => {
  const {
    open,
    onClose,
    newRequest,
    handleInputChange,
    rfqId: initialRfqId = null,
    onUpdateRfq,
    onDenyRfq,
    onApproveRfq,
    reviewMode = false,
    preflight = null,
  } = props;

  const [rfqId, setRfqId] = useState(initialRfqId);
  const [localRequest, setLocalRequest] = useState(newRequest);
  const { workscopes, accounts, uoms, taxCodes, loading: dropdownLoading } = useFetchNetsuiteDropdown();
  const { projects, loading: projectsLoading } = useFetchProjectsProcurement();
  const { vendors, loading: vendorsLoading } = useFetchVendorsProcurement();

  const [rowData, setRowData] = useState([]);
  const [originalRowData, setOriginalRowData] = useState([]);
  const [deletedRowIds, setDeletedRowIds] = useState([]);
  const deleteLock = useRef(new Set());
  const [rowsToAdd, setRowsToAdd] = useState(1);
  const mainGridApiRef = useRef(null);

  const [baseProjectId, setBaseProjectId] = useState('');
  const [baseSubsidiaryId, setBaseSubsidiaryId] = useState('');
  const [selectedProjectSubsidiaryPairs, setSelectedProjectSubsidiaryPairs] = useState([]);
  const [selectedProjectIdForSubsidiary, setSelectedProjectIdForSubsidiary] = useState('');
  const [subsidiaryOptions, setSubsidiaryOptions] = useState([]);

  const [retainageChecked, setRetainageChecked] = useState(false);
  const [retainagePercentage, setRetainagePercentage] = useState('0');
  const [resaleCertIssued, setResaleCertIssued] = useState(false);
  const [allInTaxRate, setAllInTaxRate] = useState('0');
  const [longitude, setLongitude] = useState('0');
  const [latitude, setLatitude] = useState('0');
  const [county, setCounty] = useState('');

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [pendingVendor, setPendingVendor] = useState(null);
  const [confirmVendorChangeOpen, setConfirmVendorChangeOpen] = useState(false);

  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState([]);

  const [expandGridOpen, setExpandGridOpen] = useState(false);

  const [isLoaded, setIsLoaded] = useState(!initialRfqId);
  const [isSaving, setIsSaving] = useState(false);
  const [denyReasonOpen, setDenyReasonOpen] = useState(false);
  const [saveConfirmDialogOpen, setSaveConfirmDialogOpen] = useState(false);
  const [submitConfirmDialogOpen, setSubmitConfirmDialogOpen] = useState(false);
  const [confirmExitWithoutSavingOpen, setConfirmExitWithoutSavingOpen] = useState(false);
  const [totalCost, setTotalCost] = useState(0);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const handleLatitudeBlur = (e) => {
    const n = parseFloat(e.target.value);
    if (!isNaN(n)) {
      // clamp between 0 and 90
      const clamped = Math.min(Math.max(n, 0), 90);
      // format to exactly 6 decimals
      setLatitude(clamped.toFixed(6));
    }
  };

  const handleLongitudeBlur = (e) => {
    const n = parseFloat(e.target.value);
    if (!isNaN(n)) {
      // clamp between -180 and 0
      let clamped = Math.min(Math.max(n, -180), 0);
      // if user typed a positive, make it negative
      if (clamped > 0) clamped = -clamped;
      setLongitude(clamped.toFixed(6));
    }
  };

  useEffect(() => {
    if (!initialRfqId && preflight) {
      setSelectedProjectSubsidiaryPairs([preflight.pair]);
      setBaseProjectId(preflight.baseProjectId);
      setBaseSubsidiaryId(preflight.baseSubsidiaryId);
      setLocalRequest((prev) => ({
        ...prev,
        name: preflight.rfqName,
        baseProjectId: preflight.baseProjectId,
        baseSubsidiaryId: preflight.baseSubsidiaryId,
      }));
    }
  }, [initialRfqId, preflight]);

  useEffect(() => {
    if (
      baseProjectId &&
      baseSubsidiaryId &&
      projects.length > 0 &&
      !selectedProjectSubsidiaryPairs.some(
        (pair) => pair.projectId === baseProjectId && pair.subsidiaryId === baseSubsidiaryId
      )
    ) {
      const baseProj = projects.find((p) => p.projectId === baseProjectId);
      if (baseProj) {
        handleProjectSelect({ target: { value: baseProjectId } });
      }
    }
  }, [baseProjectId, baseSubsidiaryId, projects, selectedProjectSubsidiaryPairs]);

  useEffect(() => {
    const sum = rowData.reduce((acc, r) => {
      return acc + (r.totalPrice || 0) * (1 + (r.taxRate || 0));
    }, 0);
    setTotalCost(sum);
  }, [rowData]);

  useEffect(() => {
    if (!expandGridOpen) {
      setTimeout(() => {
        if (mainGridApiRef.current) {
          // Manually force grid to rebind its API
          mainGridApiRef.current.refreshCells?.({ force: true });
        }
      }, 0);
    }
  }, [expandGridOpen]);

  useEffect(() => {
    const loadExistingRfq = async () => {
      if (!initialRfqId || dropdownLoading || workscopes.length === 0) return;

      try {
        const headers = { 'Content-Type': 'application/json' };
        const [rfqDetailsRes, rfqHeaderRes] = await Promise.all([
          axios.get(`/api/VendorAssignedRfq/RfqDetails/Dataset/${initialRfqId}`, { headers }),
          axios.get(`/api/RfqHeader/rfq?rfqId=${initialRfqId}`, { headers }),
        ]);

        const header = rfqHeaderRes.data[0] || [];
        const projectList = header.projects || [];
        const pairsList = header.rfqProjectSubsidiary.projectSubsidiaryMap || [];
        const data = rfqDetailsRes.data || [];
        const base = data[0];

        const vendor = base
          ? {
              vendorId: base.nsVendorId,
              nsVendorId: base.vendorIdInt,
              companyName: base.nsVendorName,
            }
          : null;

        const rows = data
          .filter((item) => item.poLineItemId)
          .sort((a, b) => (a.poliLineItemNumber ?? 0) - (b.poliLineItemNumber ?? 0))
          .map((item) => {
            const matchingProject = projectList.find((p) => p.projectId === item.poliProjectId);
            const matchingPair = pairsList.find(
              (p) => p.projectId == item.poliProjectId && p.subsidiaryId == item.poliNetsuiteSubsidiaryId
            );
            const quantity = item.poliQuantity ?? 0;
            const unitPrice = item.vendorUnitPrice ?? 0;
            const taxRate = item.taxRate ?? 0;
            const totalPrice = quantity * unitPrice;

            return {
              lineItemNumber: item.poliLineItemNumber ?? 0,
              id: item.poLineItemId,
              projectId: matchingProject?.projectId,
              netsuiteBudgetId: item.poliNetsuiteBudgetId,
              lineItemSubsidiaryId: item.poliNetsuiteSubsidiaryId,
              selectedProject: matchingProject?.name || '',
              selectedSubsidiary: matchingPair?.subsidiaryName || '',
              materialCategory: item.poliMaterialCategory,
              quantity,
              unitPrice,
              totalPrice,
              uom: item.poliUom,
              description: item.poliDescription,
              comments: item.comments,
              manufacturer: item.vendorManufacturer,
              model: item.vendorModel,
              leadTime: item.vendorLeadTime,
              taxRate,
              taxCode: item.taxCode,
              hideItemQtyRate: !!item.poliHideItemQtyRate,
            };
          });

        const attachementsRes = await axios.get(`/api/RfqHeader/fetchAttachments/${initialRfqId}`, { headers });

        const pairs = pairsList.map((proj) => ({
          projectId: proj.projectId,
          budgetId: proj.budgetId,
          subsidiaryId: proj.subsidiaryId,
          subsidiaryName: proj.subsidiaryName,
        }));

        setRfqId(initialRfqId);
        setBaseProjectId(header.baseProjectId || '');
        setBaseSubsidiaryId(header.baseSubsidiaryId || '');
        setSelectedProjectSubsidiaryPairs(pairs);
        setSelectedVendor(vendor);
        setRetainageChecked(header.retainageChecked ?? false);
        setRetainagePercentage(header.retainagePercentage?.toString() ?? '0');
        setResaleCertIssued(header.resaleCertIssued ?? false);
        setAllInTaxRate(header.allInTaxRate?.toString() ?? '0');
        setLongitude(header.longitude?.toString() ?? '0');
        setLatitude(header.latitude?.toString() ?? '0');
        setCounty(header.county?.toString() || '');
        setRowData(rows);
        setOriginalRowData(JSON.parse(JSON.stringify(rows)));
        setExistingAttachments(attachementsRes.data || []);

        const matchingScope = workscopes.find((ws) => ws.name === header.workScope);
        const workScopeId = matchingScope?.id ?? '';

        handleInputChange({ target: { name: 'name', value: header.name } });
        handleInputChange({ target: { name: 'description', value: header.description } });
        handleInputChange({ target: { name: 'deliveryLocation', value: header.deliveryLocation } });
        handleInputChange({ target: { name: 'workScope', value: workScopeId } });
        handleInputChange({ target: { name: 'formType', value: header.formType } });
        handleInputChange({ target: { name: 'vendorInstructions', value: header.vendorInstuctions } });
        setLocalRequest({
          name: header.name,
          description: header.description,
          vendorInstructions: header.vendorInstructions,
          deliveryLocation: header.deliveryLocation,
          workScope: workScopeId,
          formType: header.formType,
          proposalDueDate: header.proposalDueDate,
          desiredDeliveryDate: header.desiredDeliveryDate,
          baseProjectId: header.baseProjectId,
          baseSubsidiaryId: header.baseSubsidiaryId,
        });

        // Refresh grid
        setTimeout(() => {
          mainGridApiRef.current.refreshCells({ force: true });
        }, 0);

        setIsLoaded(true);
      } catch (err) {
        toast.error('Failed to load request data.');
        console.error(err);
      }
    };

    loadExistingRfq();
  }, [initialRfqId, dropdownLoading, workscopes.length]);

  useEffect(() => {
    if (!retainageChecked) {
      setRetainagePercentage('0');
      setAllInTaxRate('0');
      setLongitude('');
      setLatitude('');
      setCounty('');
    }
  }, [retainageChecked]);

  const haveLineItemChanges = (original, updated) => {
    const keysToCompare = [
      'projectId',
      'netsuiteBudgetId',
      'lineItemSubsidiaryId',
      'materialCategory',
      'uom',
      'hideItemQtyRate',
      'description',
      'quantity',
      'unitPrice',
      'comments',
      'manufacturer',
      'model',
      'leadTime',
      'taxRate',
      'taxCode',
      'lineItemNumber',
    ];

    return keysToCompare.some((key) => {
      const originalVal = original?.[key];
      const updatedVal = updated?.[key];

      // Handle number comparison
      if (typeof originalVal === 'number' && typeof updatedVal === 'number') {
        return Math.abs(originalVal - updatedVal) > 0.0001;
      }

      return originalVal !== updatedVal;
    });
  };

  function currencyParser(params) {
    let value = params.newValue;
    if (value == null || value === '') return null;

    value = String(value).replace(/[$,]/g, ''); // Remove both $ and ,
    return parseFloat(value);
  }

  const columnDefs = useMemo(() => {
    const itemNames = uoms.map((item) => item.name).concat(['']);
    const acctNames = accounts.map((item) => item.fullname);
    const selectedProjectNames = Array.from(
      new Set(
        selectedProjectSubsidiaryPairs
          .map((pair) => {
            const proj = projects.find((p) => p.projectId === pair.projectId);
            return proj ? proj.name : null;
          })
          .filter((name) => name !== null)
      )
    );

    const pushRowsChanged = (params) => {
      setTimeout(() => {
        params.api.refreshCells({ rowNodes: [params.node], force: true });
      }, 0);
    };

    return [
      {
        headerName: '',
        field: 'delete',
        cellRenderer: (params) => (
          <IconButton size="small" onClick={() => handleDeleteRow(params.node.rowIndex)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
        filter: false,
        width: 50,
        suppressSizeToFit: true,
      },
      {
        headerName: 'Line #',
        field: 'lineItemNumber',
        width: 125,
        editable: false,
        valueGetter: (params) => params.data.lineItemNumber ?? params.node.rowIndex + 1,
      },
      {
        headerName: 'ID',
        field: 'id',
        hide: true,
      },
      {
        headerName: 'Project ID',
        field: 'projectId',
        hide: true,
      },
      {
        headerName: 'NetSuite Budget ID',
        field: 'netsuiteBudgetId',
        hide: true,
      },
      {
        headerName: 'NetSuite Subsidiary ID',
        field: 'lineItemSubsidiaryId',
        hide: true,
      },
      {
        headerName: 'Project',
        field: 'selectedProject',
        editable: true,
        width: 250,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: selectedProjectNames },
        valueGetter: (params) => {
          const match = selectedProjectSubsidiaryPairs.find((p) => p.projectId === params.data.projectId);
          return match ? projects.find((proj) => proj.projectId === match.projectId)?.name || '' : '';
        },
        valueSetter: (params) => {
          const selectedPair = selectedProjectSubsidiaryPairs.find((pair) => {
            const project = projects.find((p) => p.projectId === pair.projectId);
            return project?.name === params.newValue;
          });

          if (selectedPair) {
            const selectedProj = projects.find((p) => p.projectId === selectedPair.projectId);

            params.data.projectId = selectedPair.projectId;
            params.data.netsuiteBudgetId = selectedPair.budgetId;
            params.data.selectedProject = selectedProj?.name || '';
            return true;
          }

          return false;
        },
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Subsidiary',
        field: 'selectedSubsidiary',
        editable: true,
        width: 250,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: (params) => {
          // Filter available subsidiaries only for the current row's project
          const projectId = params.data.projectId;
          const subsidiaries = selectedProjectSubsidiaryPairs
            .filter((pair) => pair.projectId === projectId)
            .map((pair) => pair.subsidiaryName);

          return { values: subsidiaries };
        },
        valueGetter: (params) => {
          const { projectId, lineItemSubsidiaryId } = params.data;

          const match = selectedProjectSubsidiaryPairs.find(
            (p) => String(p.projectId) === String(projectId) && String(p.subsidiaryId) === String(lineItemSubsidiaryId)
          );

          return match?.subsidiaryName || '';
        },
        valueSetter: (params) => {
          const match = selectedProjectSubsidiaryPairs.find(
            (p) => p.projectId === params.data.projectId && p.subsidiaryName === params.newValue
          );
          if (match) {
            params.data.lineItemSubsidiaryId = match.subsidiaryId;
            return true;
          }
          return false;
        },
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Material Category',
        field: 'materialCategory',
        editable: true,
        width: 450,
        cellEditor: 'agRichSelectCellEditor',
        // cellEditorParams: { values: acctNames },
        cellEditorParams: {
          values: acctNames,
          searchType: 'matchAny',
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
        },
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Description',
        field: 'description',
        editable: true,
        width: 500,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'normal' },
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'UOM',
        field: 'uom',
        editable: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: itemNames,
          searchType: 'matchAny',
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
        },
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Hide Item Qty/Rate',
        field: 'hideItemQtyRate',
        editable: true,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Quantity',
        field: 'quantity',
        editable: true,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { preventStepping: true },
        valueSetter: (params) => {
          const newVal = parseFloat(params.newValue);
          if (!isNaN(newVal)) {
            params.data.quantity = newVal;
            params.data.totalPrice = (params.data.unitPrice || 0) * newVal;
            return true;
          }
          return false;
        },
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Unit Price',
        field: 'unitPrice',
        editable: true,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { preventStepping: true },
        valueFormatter: (params) =>
          params.value
            ? `$${params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '',
        valueSetter: (params) => {
          const raw = params.newValue?.toString().replace(/[$,]/g, ''); // removes $ and commas
          const newVal = parseFloat(raw);
          if (!isNaN(newVal)) {
            params.data.unitPrice = newVal;
            params.data.totalPrice = (params.data.quantity || 0) * newVal;
            return true;
          }
          return false;
        },
        valueParser: currencyParser,
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Ext Price',
        field: 'totalPrice',
        valueGetter: (params) => params.data.unitPrice * params.data.quantity || '',
        valueFormatter: (params) =>
          params.value
            ? `$${params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '',
      },
      {
        headerName: 'Tax Rate',
        field: 'taxCode',
        editable: true,
        width: 200,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: taxCodes.map((tc) => tc.value),
          searchType: 'matchAny',
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          formatValue: (value) => {
            const match = taxCodes.find((tc) => tc.value === value);
            return match ? `(${(match.rate * 100).toFixed(2)}%)` : value;
          },
        },
        valueFormatter: (params) => {
          const match = taxCodes.find((tc) => tc.value === params.value);
          return match ? `(${(match.rate * 100).toFixed(2)}%)` : params.value;
        },
        valueSetter: (params) => {
          const input = params.newValue?.toString().trim();

          // Match either taxCode or tax label (e.g. "-7" or "(7.00%)")
          const matchByCode = taxCodes.find((tc) => tc.value === input);
          const matchByLabel = taxCodes.find((tc) => `(${(tc.rate * 100).toFixed(2)}%)` === input);

          const match = matchByCode || matchByLabel;

          if (match) {
            params.data.taxCode = match.value;
            params.data.taxRate = match.rate;
            return true;
          }

          // If no match, reset
          params.data.taxCode = null;
          params.data.taxRate = 0;
          return true;
        },
        onCellValueChanged: (params) => {
          const selected = taxCodes.find((tc) => tc.value === params.newValue);
          if (selected) {
            params.data.taxRate = selected.rate;
            params.data.taxCode = selected.value;
          } else {
            params.data.taxRate = 0;
            params.data.taxCode = null;
          }

          params.api.refreshCells({
            rowNodes: [params.node],
            columns: ['taxAmount', 'awardedPlusTax'],
            force: true,
          });

          setRowData((prev) => [...prev]);
        },
      },
      {
        headerName: 'Tax Amount',
        field: 'taxAmount',
        editable: false,
        valueFormatter: (params) =>
          params.value
            ? `$${params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '',
        valueGetter: (params) => {
          const taxRate = params.data.taxRate ?? 0;
          const awardedPrice = params.data.totalPrice ?? 0;
          return taxRate * awardedPrice;
        },
      },
      {
        headerName: 'Awarded Ext Price + Tax',
        field: 'awardedPlusTax',
        editable: false,
        width: 300,
        valueFormatter: (params) =>
          params.value
            ? `$${params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '',
        valueGetter: (params) => {
          const awardedPrice = params.data.totalPrice ?? 0;
          const taxRate = params.data.taxRate ?? 0;
          return awardedPrice + taxRate * awardedPrice;
        },
      },
      {
        headerName: 'Vendor Comments',
        field: 'comments',
        editable: true,
        width: 500,
        wrapText: true,
        cellStyle: { whiteSpace: 'normal' },
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Manufacturer',
        field: 'manufacturer',
        width: 250,
        editable: true,
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Model',
        field: 'model',
        width: 250,
        editable: true,
        onCellValueChanged: pushRowsChanged,
      },
      {
        headerName: 'Lead Time (Days)',
        field: 'leadTime',
        editable: true,
        onCellValueChanged: pushRowsChanged,
      },
    ];
  }, [uoms, accounts, selectedProjectSubsidiaryPairs, projects]);

  const handleLocalInputChange = (e) => {
    const { name, value } = e.target;
    setLocalRequest((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocalDateChange = (name, date) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      setLocalRequest((prev) => ({
        ...prev,
        [name]: date.toISOString(),
      }));
    } else {
      setLocalRequest((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setNewAttachments((prev) => [...prev, ...files]);
    event.target.value = null;
  };

  const handleRemoveAttachment = (file) => {
    if (file.blobName) {
      setAttachmentsToDelete((prev) => [...prev, file]);
      setExistingAttachments((prev) => prev.filter((f) => f.blobName !== file.blobName));
    } else {
      setNewAttachments((prev) => prev.filter((f) => f.name !== file.name));
    }
  };

  const handleAddRow = () => {
    const count = Math.min(Math.max(parseInt(rowsToAdd), 1), 100);
    const defaultProject = selectedProjectSubsidiaryPairs.find(
      (pair) => pair.projectId === baseProjectId && pair.subsidiaryId === baseSubsidiaryId
    );
    const defaultProjDetails = projects.find((p) => p.projectId === baseProjectId);
    const defaultTaxCode = '-7';
    const defaultTaxRate = 0;

    const existingMaxLineNumber = rowData.length > 0 ? Math.max(...rowData.map((row) => row.lineItemNumber || 0)) : 0;

    const newRows = Array.from({ length: count }, (_, i) => ({
      lineItemNumber: existingMaxLineNumber + i + 1,
      description: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      taxCode: defaultTaxCode,
      taxRate: defaultTaxRate,
      projectId: defaultProject?.projectId || '',
      netsuiteBudgetId: defaultProject?.budgetId ? parseInt(defaultProject.budgetId) : '',
      lineItemSubsidiaryId: defaultProject?.subsidiaryId ? parseInt(defaultProject.subsidiaryId) : '',
      selectedProject: defaultProjDetails?.name || '',
      selectedSubsidiary: defaultProject?.subsidiaryName || '',
      uom: '',
      hideItemQtyRate: false,
    }));

    setRowData((prev) => [...prev, ...newRows]);
  };

  const handleProjectSelect = async (event) => {
    const projectId = event.target.value;
    const project = projects.find((p) => p.projectId === projectId);
    if (!project) return;

    try {
      const response = await axios.get(
        `/api/NetSuite/CustomRecordPath?customPath=classification/${project.netSuiteId}/subsidiary&useProduction=${useNetSuiteProductionRfq}`
      );

      const items = response.data?.items || [];

      if (items.length > 0) {
        setSelectedProjectIdForSubsidiary(projectId);
        setSubsidiaryOptions(items);
      } else {
        await runSubsidiaryFallback(project, projectId);
      }
    } catch {
      await runSubsidiaryFallback(project, projectId);
    }
  };

  const runSubsidiaryFallback = async (project, projectId) => {
    try {
      const backupRes = await axios.get(
        `/api/NetSuite/CustomRecordPath?customPath=classification/${project.netSuiteId}&useProduction=${useNetSuiteProductionRfq}`
      );
      const backupSubsidiary = [backupRes.data?.custrecord138];

      setSelectedProjectIdForSubsidiary(projectId);
      setSubsidiaryOptions(backupSubsidiary);
    } catch (err) {
      console.error('Fallback subsidiary fetch failed:', err);
      toast.error('Failed to fetch fallback subsidiary.');
    }
  };

  const handleRemoveProject = (projectId, subsidiaryId) => {
    if (projectId === baseProjectId && subsidiaryId === baseSubsidiaryId) {
      toast.warning('Base project cannot be removed from the request.');
      return;
    }

    setSelectedProjectSubsidiaryPairs((prev) =>
      prev.filter((pair) => !(pair.projectId === projectId && pair.subsidiaryId === subsidiaryId))
    );
  };

  const handleDeleteRow = (rowIndex) => {
    if (deleteLock.current.has(rowIndex)) return;

    try {
      const rowNode = mainGridApiRef.current.getDisplayedRowAtIndex(rowIndex);
      const rowToDelete = rowNode?.data;
      if (!rowToDelete) return;

      deleteLock.current.add(rowIndex);
      setTimeout(() => deleteLock.current.delete(rowIndex), 250);

      if (rowToDelete.id) {
        setDeletedRowIds((prev) => [...prev, rowToDelete.id]);
      }

      // Remove from ag-Grid
      mainGridApiRef.current.applyTransaction({ remove: [rowToDelete] });

      // Update local state and re-number lines
      setRowData((prev) => {
        const updated = prev.filter((r) => r !== rowToDelete);
        return updated.map((item, i) => ({
          ...item,
          lineItemNumber: i + 1,
        }));
      });
    } catch (err) {
      console.error('Error during row deletion:', err);
      toast.error('Failed to delete row due to internal error.');
    }
  };

  const handleVendorSelect = async (event) => {
    const vendorId = event.target.value;
    const newVendor = vendors.find((v) => v.nsVendorId === vendorId);

    const hasAnyAttachments = existingAttachments.length > 0 || newAttachments.length > 0;

    if (selectedVendor && selectedVendor.nsVendorId !== vendorId && hasAnyAttachments) {
      setPendingVendor(newVendor); // store temporarily
      setConfirmVendorChangeOpen(true); // show dialog only if attachments are present
      return;
    }

    // otherwise just change vendor immediately
    setSelectedVendor(newVendor || null);
    setPendingVendor(null);
  };

  const handleVendorChangeProceed = () => {
    setConfirmVendorChangeOpen(false);
    setAttachmentsToDelete((prev) => [...prev, ...existingAttachments]);
    setExistingAttachments([]);
    setNewAttachments([]);
    setSelectedVendor(pendingVendor);
    setPendingVendor(null);
  };

  const handleVendorChangeCancel = () => {
    setConfirmVendorChangeOpen(false);
    setPendingVendor(null);
  };

  const handleSubmitForApproval = async () => {
    const savedRequest = await handleSaveProgress(); // Save first

    if (!savedRequest.rfqId) {
      toast.error('Missing request ID.');
      return;
    }

    try {
      await axios.put(
        `/api/RfqHeader/rfq-approval-needed`,
        {
          id: savedRequest.rfqId,
          readyForApproval: true,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      toast.success('Request submitted for approval.');

      if (onUpdateRfq) {
        await onUpdateRfq();
      }

      if (onClose) onClose(); // Close modal
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else {
        toast.error('Failed to submit for approval.');
      }
    }
  };

  const formatAsYyyyMmDd = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().split('T')[0];
  };

  const handleSubsidiarySelect = (subsidiaryId) => {
    const subsidiary = subsidiaryOptions.find((s) => s.id === subsidiaryId);
    const project = projects.find((p) => p.projectId === selectedProjectIdForSubsidiary);
    if (!project || !subsidiary) return;

    const alreadyExists = selectedProjectSubsidiaryPairs.some(
      (pair) => pair.projectId === project.projectId && pair.subsidiaryId === subsidiary.id
    );
    if (alreadyExists) return;

    const newPair = {
      projectId: project.projectId,
      budgetId: project.netSuiteId,
      subsidiaryId: subsidiary.id,
      subsidiaryName: subsidiary.refName,
    };

    setSelectedProjectSubsidiaryPairs((prev) => [...prev, newPair]);
    setSubsidiaryOptions([]);
    setSelectedProjectIdForSubsidiary('');
  };

  const validateHeaderFields = () => {
    const missingFields = [];
    if (!localRequest.name) missingFields.push('Name');
    if (!localRequest.description) missingFields.push('Description');
    if (!localRequest.deliveryLocation) missingFields.push('Delivery Location');
    if (!localRequest.workScope) missingFields.push('Work Scope');
    if (!localRequest.formType) missingFields.push('Form Type');
    if (!localRequest.desiredDeliveryDate) missingFields.push('Desired Delivery / Service Completion Date');
    if (!baseProjectId) missingFields.push('Base Project');

    if (rowData.length > 0 && !selectedVendor) {
      missingFields.push('Vendor (required when line items exist)');
    }

    return missingFields;
  };

  const handleImportExcel = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json(firstSheet, { defval: '', range: 2 });

      const nextLineNumber = rowData.length > 0 ? Math.max(...rowData.map((r) => r.lineItemNumber || 0)) + 1 : 1;

      const errors = [];
      const importedRows = rawRows.map((row, idx) => {
        const lineNumber = nextLineNumber + idx;

        const matchingProject = projects.find(
          (p) => p.name.trim().toLowerCase() === (row['Project'] || '').trim().toLowerCase()
        );
        const matchingSubsidiary = selectedProjectSubsidiaryPairs.find(
          (p) => p.subsidiaryName.trim().toLowerCase() === (row['Subsidiary'] || '').trim().toLowerCase()
        );

        const matchingPair = selectedProjectSubsidiaryPairs.find(
          (pair) =>
            pair.projectId === matchingProject?.projectId && pair.subsidiaryName === matchingSubsidiary.subsidiaryName
        );
        const matchingAccount = accounts.find(
          (a) => a.fullname.trim().toLowerCase() === (row['Material Category'] || '').trim().toLowerCase()
        );
        const matchingUom = uoms.find((u) => u.name.trim().toLowerCase() === (row['UOM'] || '').trim().toLowerCase());
        const matchingTax = taxCodes.find(
          (tc) =>
            tc.text?.trim().toLowerCase() ===
            String(row['Tax Rate'] || '')
              .trim()
              .toLowerCase()
        );

        if (!matchingProject) errors.push(`Row ${idx + 2}: Invalid project "${row['Project']}"`);
        if (!matchingAccount) errors.push(`Row ${idx + 2}: Invalid material category "${row['Material Category']}"`);
        if (!matchingUom) errors.push(`Row ${idx + 2}: Invalid UOM "${row['UOM']}"`);
        if (!matchingTax) errors.push(`Row ${idx + 2}: Invalid Tax Rate "${row['Tax Rate']}"`);

        return {
          lineItemNumber: lineNumber,
          projectId: matchingPair?.projectId || '',
          netsuiteBudgetId: matchingPair?.budgetId || '',
          lineItemSubsidiaryId: matchingPair?.subsidiaryId || '',
          selectedProject: matchingProject?.name || '',
          selectedSubsidiary: matchingPair?.subsidiaryName || '',
          materialCategory: matchingAccount?.fullname || '',
          uom: matchingUom?.name || '',
          hideItemQtyRate: false,
          quantity: parseFloat(row['Quantity']) || 1,
          unitPrice: parseFloat(row['Unit Price']) || 0,
          description: row['Description'] || '',
          comments: row['Vendor Comments'] || '',
          manufacturer: row['Manufacturer'] || '',
          model: row['Model'] || '',
          leadTime: row['Lead Time'] || '',
          taxCode: matchingTax?.value || '-7',
          taxRate: matchingTax?.rate || 0,
        };
      });

      if (errors.length > 0) {
        toast.error(`Import failed:\n${errors.join('\n')}`);
      } else {
        const finalImportedRows = importedRows.map((row, idx) => {
          const totalPrice = (row.unitPrice || 0) * (row.quantity || 0);
          const taxAmount = totalPrice * (row.taxRate || 0);
          const awardedPlusTax = totalPrice + taxAmount;

          return {
            ...row,
            lineItemNumber: idx + 1,
            totalPrice,
            taxAmount,
            awardedPlusTax,
          };
        });

        setRowData((prev) => {
          const existingMaxLineNumber = prev.length > 0 ? Math.max(...prev.map((r) => r.lineItemNumber || 0)) : 0;

          const adjustedRows = finalImportedRows.map((row, idx) => ({
            ...row,
            lineItemNumber: existingMaxLineNumber + idx + 1,
          }));

          return [...prev, ...adjustedRows];
        });
        toast.success(`Imported ${finalImportedRows.length} row(s)`);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSaveProgress = async () => {
    if (isSaving) return rfqId;
    setIsSaving(true);
    const missingFields = validateHeaderFields();

    if (missingFields.length > 0) {
      toast.error(`Missing required inputs: ${missingFields.join(', ')}`);
      setIsSaving(false);
      return;
    }

    const incompleteRows = rowData.filter(
      (row) => !row.projectId || !row.lineItemSubsidiaryId || !row.selectedProject || !row.selectedSubsidiary
    );

    if (incompleteRows.length > 0) {
      toast.error('Some line items are missing project and/or subsidiary selection.');
      setIsSaving(false);
      return;
    }

    const requiredFields = ['materialCategory', 'description', 'quantity'];
    const fieldToHeaderMap = {};
    columnDefs.forEach((col) => {
      if (col.field && col.headerName) {
        fieldToHeaderMap[col.field] = col.headerName;
      }
    });

    const rowsMissingRequiredFields = rowData
      .map((row, index) => {
        const missing = requiredFields.filter((field) => {
          const value = row[field];
          if (typeof value === 'number') return isNaN(value);
          return value === '' || value === null || value === undefined;
        });
        return { index, missing };
      })
      .filter((row) => row.missing.length > 0);

    if (rowsMissingRequiredFields.length > 0) {
      const errorMsg = rowsMissingRequiredFields
        .map(
          ({ index, missing }) =>
            `Row ${index + 1}: Missing ${missing.map((field) => fieldToHeaderMap[field] || field).join(', ')}`
        )
        .join('\n');

      toast.error(`Validation failed:\n${errorMsg}`);
      setIsSaving(false);
      return;
    }

    let currentRfqId = rfqId;

    const selectedWorkScope = workscopes.find((ws) => ws.id === localRequest.workScope);
    const workScopeNameToSave = selectedWorkScope?.name || '';

    try {
      if (!currentRfqId) {
        const headerPayload = {
          name: localRequest.name,
          rfqType: 'PO Request',
          formType: localRequest.formType,
          workScope: workScopeNameToSave,
          description: localRequest.description,
          vendorInstructions: localRequest.vendorInstructions || '',
          proposalDueDate: formatAsYyyyMmDd(
            localRequest.proposalDueDate ? new Date(localRequest.proposalDueDate) : new Date()
          ),
          desiredDeliveryDate: formatAsYyyyMmDd(new Date(localRequest.desiredDeliveryDate)),
          deliveryLocation: localRequest.deliveryLocation,
          readyForApproval: false,
          baseProjectId: baseProjectId,
          baseSubsidiaryId: baseSubsidiaryId,
          retainageChecked: retainageChecked,
          retainagePercentage: toFixedNumber(retainagePercentage, 5) || 0,
          resaleCertIssued: resaleCertIssued,
          allInTaxRate: toFixedNumber(allInTaxRate, 5) || 0,
          latitude: toFixedNumber(latitude, 6) || 0,
          longitude: toFixedNumber(longitude, 6) || 0,
          county: county,
        };

        const headerResponse = await axios.post(`/api/RfqHeader/new-rfq`, headerPayload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        currentRfqId = headerResponse.data;
        setRfqId(currentRfqId);
      } else {
        const headerPayload = {
          name: localRequest.name,
          baseProjectId: baseProjectId,
          baseSubsidiaryId: baseSubsidiaryId,
          rfqType: 'PO Request',
          formType: localRequest.formType,
          workScope: workScopeNameToSave,
          description: localRequest.description,
          vendorInstructions: localRequest.vendorInstructions || '',
          proposalDueDate: formatAsYyyyMmDd(
            localRequest.proposalDueDate ? new Date(localRequest.proposalDueDate) : new Date()
          ),
          desiredDeliveryDate: formatAsYyyyMmDd(new Date(localRequest.desiredDeliveryDate)),
          deliveryLocation: localRequest.deliveryLocation,
          retainageChecked: retainageChecked,
          retainagePercentage: toFixedNumber(retainagePercentage, 5) || 0,
          resaleCertIssued: resaleCertIssued,
          allInTaxRate: toFixedNumber(allInTaxRate, 5) || 0,
          latitude: toFixedNumber(latitude, 6) || 0,
          longitude: toFixedNumber(longitude, 6) || 0,
          county: county,
        };

        await axios.put(`/api/RfqHeader/rfq/${currentRfqId}`, headerPayload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }

      const effectiveRfqId = currentRfqId || rfqId;
      //update rfqProjects linkage
      await axios.post(
        `/api/RfqHeader/rfq/manageProjects`,
        {
          rfqId: effectiveRfqId,
          projectSubsidiaryMap: selectedProjectSubsidiaryPairs,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // 4️⃣ Assign Vendor (if selected)
      if (selectedVendor) {
        const vendorPayload = {
          nsVendorId: selectedVendor.nsVendorId,
          nsVendorName: selectedVendor.companyName,
          rfqId: effectiveRfqId,
        };
        await axios.post(`/api/VendorAssignedRfq/PostRecord?isPoRequest=true`, vendorPayload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }

      //Save Attachements
      const uploadForm = new FormData();
      newAttachments.forEach((file) => uploadForm.append('files', file));

      if (newAttachments.length > 0) {
        await axios.post(`/api/RfqHeader/uploadAttachments/${effectiveRfqId}`, uploadForm, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      for (const file of attachmentsToDelete) {
        const actualFileName = file.blobName.split('/').pop();

        await axios.delete(`/api/RfqHeader/deleteFile/${effectiveRfqId}/${encodeURIComponent(actualFileName)}`);
      }

      //refresh attachment lists
      setNewAttachments([]);
      setAttachmentsToDelete([]);
      const updatedAttachmentsRes = await axios.get(`/api/RfqHeader/fetchAttachments/${effectiveRfqId}`);
      setExistingAttachments(updatedAttachmentsRes.data || []);

      //Resolve any deleted rows
      for (const id of deletedRowIds) {
        try {
          await axios.delete(`/api/PurchaseOrderLineItems/po-lineitem-delete/${id}`);
        } catch (err) {
          console.error(err);
          if (err.response?.status === 403) {
            toast.error('Elevated permissions required for this action.');
          } else {
            toast.error('Error deleting line item.');
          }
        }
      }
      setDeletedRowIds([]);

      // 5️⃣ Save Line Items
      const newRows = rowData.filter((row) => !row.id);
      const existingRows = rowData.filter((row) => row.id);

      // 1️⃣ Create & Update New Rows
      for (const row of newRows) {
        const createPayload = {
          rfqId: effectiveRfqId,
          projectId: row.projectId,
          netsuiteBudgetId: row.netsuiteBudgetId,
          netsuiteSubsidiaryId: row.lineItemSubsidiaryId,
          lineItemNumber: row.lineItemNumber,
        };

        const postResponse = await axios.post(
          `/api/PurchaseOrderLineItems/po-lineitem?isPoRequest=true&vendorId=${selectedVendor.vendorId}`,
          createPayload,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const newId = postResponse.data; // Since you updated the API to return the Guid directly

        if (!newId) continue;

        row.id = newId;

        const updateLineItemPayload = {
          projectId: row.projectId,
          netsuiteBudgetId: row.netsuiteBudgetId,
          netsuiteSubsidiaryId: row.lineItemSubsidiaryId,
          materialCategory: row.materialCategory,
          uom: row.uom,
          hideItemQtyRate: row.hideItemQtyRate,
          description: row.description,
          quantity: row.quantity,
          lineItemNumber: row.lineItemNumber,
        };

        await axios.put(`/api/PurchaseOrderLineItems/po-lineitem/${newId}`, updateLineItemPayload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const selectedTax = taxCodes.find((tc) => tc.value === row.taxCode);

        const vendorBidPayload = {
          unitPrice: row.unitPrice,
          comments: row.comments,
          manufacturer: row.manufacturer,
          model: row.model,
          leadTime: row.leadTime,
          vendorQuantity: row.quantity,
          extPrice: row.quantity * row.unitPrice,
          taxCode: selectedTax?.value ?? '-7',
          taxRate: selectedTax?.rate ?? 0,
        };

        await axios.put(`/api/RfqVendorBidSheet/bidsheet/line/${newId}`, vendorBidPayload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }

      // 2️⃣ Update Existing Rows
      for (const row of existingRows) {
        const original = originalRowData.find((o) => o.id === row.id);
        if (!original || haveLineItemChanges(original, row)) {
          const updateLineItemPayload = {
            projectId: row.projectId,
            netsuiteBudgetId: row.netsuiteBudgetId,
            netsuiteSubsidiaryId: row.lineItemSubsidiaryId,
            materialCategory: row.materialCategory,
            uom: row.uom,
            hideItemQtyRate: row.hideItemQtyRate,
            description: row.description,
            quantity: row.quantity,
            lineItemNumber: row.lineItemNumber,
          };

          await axios.put(`/api/PurchaseOrderLineItems/po-lineitem/${row.id}`, updateLineItemPayload, {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const selectedTax = taxCodes.find((tc) => tc.value === row.taxCode);

          const vendorBidPayload = {
            unitPrice: row.unitPrice,
            comments: row.comments,
            manufacturer: row.manufacturer,
            model: row.model,
            leadTime: row.leadTime,
            vendorQuantity: row.quantity,
            extPrice: row.quantity * row.unitPrice,
            taxCode: selectedTax?.value ?? '-7',
            taxRate: selectedTax?.rate ?? 0,
          };

          await axios.put(`/api/RfqVendorBidSheet/bidsheet/line/${row.id}`, vendorBidPayload, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
        }
      }
      toast.success('Progress saved successfully!');
      //setSaveConfirmDialogOpen(false);

      // if (onUpdateRfq) {
      //   await onUpdateRfq();
      // }

      // Refresh file state
      setNewAttachments([]);
      setAttachmentsToDelete([]);
      try {
        const updatedAttachmentsRes = await axios.get(`/api/RfqHeader/fetchAttachments/${effectiveRfqId}`);
        setExistingAttachments(updatedAttachmentsRes.data || []);
      } catch (err) {
        console.error('Error refreshing attachments:', err);
        toast.error('Could not refresh attachment list after save.');
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else {
        toast.error('An error occurred while saving progress.');
      }
    } finally {
      const finalId = currentRfqId || rfqId;
      setIsSaving(false);
      return {
        rfqId: finalId,
        attachments: [...existingAttachments, ...newAttachments],
        selectedProjectSubsidiaryPairs,
      };
    }
  };

  const handleSaveAndExit = async () => {
    const missingFields = validateHeaderFields();

    if (missingFields.length > 0) {
      setConfirmExitWithoutSavingOpen(true);
      return;
    }

    setIsSaving(true);
    await handleSaveProgress();
    if (onUpdateRfq) await onUpdateRfq();
    setSaveConfirmDialogOpen(false);
    setTimeout(() => {
      setIsSaving(false);
      if (onClose) onClose();
    }, 0);
  };

  const handleDenyWithNote = async (note) => {
    setDenyReasonOpen(false);

    if (!rfqId) {
      toast.error('Missing RFQ ID.');
      return;
    }

    try {
      await onDenyRfq(rfqId, note);
    } catch (err) {
      toast.error('Failed to deny request.');
      console.error(err);
    }
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: {
      textAlign: 'left',
    },
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="xxl">
      <DialogContent sx={{ padding: '16px 24px', minHeight: '80vh' }}>
        {/* Backdrop & spinner, covers entire content area while isLoaded===false */}
        <Backdrop
          sx={{
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.tooltip,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            p: 2,
          }}
          open={!isLoaded}
        >
          <CircularProgress size={64} />
          <Typography variant="h6" color="inherit" sx={{ mt: 2 }}>
            Loading Request Data...
          </Typography>
        </Backdrop>
        <Grid container spacing={1.5}>
          <Grid item xs={9}>
            <Grid container spacing={1.5}>
              {' '}
              {/* Main Grid for inputs */}
              {/* Left Column (6) - General Form Inputs & Projects */}
              <Grid item xs={6}>
                <Grid container spacing={1}>
                  {/* Name Input */}
                  <Grid item xs={12}>
                    <Grid container spacing={1}>
                      <Grid item xs={7}>
                        <TextField
                          label="PO Name"
                          fullWidth
                          size="small"
                          name="name"
                          value={localRequest.name}
                          onChange={handleLocalInputChange}
                          variant="outlined"
                          disabled={!!rfqId || !!preflight}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Autocomplete
                          filterOptions={normalizedFilterOptions}
                          options={projects}
                          getOptionLabel={(option) => `${option.name} (AFE: ${option.afeNumber})`}
                          value={projects.find((p) => p.projectId === baseProjectId) || null}
                          onChange={(_, newValue) => setBaseProjectId(newValue?.projectId || '')}
                          disabled={!!rfqId || projectsLoading || !!preflight}
                          renderInput={(params) => (
                            <TextField {...params} label="Base Project" size="small" fullWidth variant="outlined" />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Description, Delivery Location, Work Scope, Form Type */}
                  <Grid item xs={12}>
                    <TextField
                      label="Description (Netsuite Memo)"
                      fullWidth
                      size="small"
                      name="description"
                      value={localRequest.description}
                      onChange={handleLocalInputChange}
                      variant="outlined"
                      multiline
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      options={workscopes}
                      getOptionLabel={(option) => option.name}
                      value={workscopes.find((ws) => ws.id === localRequest.workScope) || null}
                      onChange={(_, newVal) => {
                        handleLocalInputChange({ target: { name: 'workScope', value: newVal?.id || '' } });
                      }}
                      loading={dropdownLoading}
                      disabled={dropdownLoading || workscopes.length === 0}
                      renderInput={(params) => (
                        <TextField {...params} label="Work Scope" fullWidth variant="outlined" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      options={['Equipment']} // Add more options as needed later
                      getOptionLabel={(option) => option}
                      value={localRequest.formType || null}
                      onChange={(_, newVal) => {
                        handleLocalInputChange({ target: { name: 'formType', value: newVal || '' } });
                      }}
                      renderInput={(params) => <TextField {...params} label="Form Type" fullWidth variant="outlined" />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={retainageChecked}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setRetainageChecked(on);
                                if (!on) setRetainagePercentage('0');
                              }}
                            />
                          }
                          label="Retainage %"
                        />
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={resaleCertIssued}
                              onChange={(e) => setResaleCertIssued(e.target.checked)}
                            />
                          }
                          label="Resale Certificate Issued"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* only show % input when checked */}
                  {(retainageChecked || resaleCertIssued) && (
                    <Grid container spacing={1} sx={{ mt: 2 }}>
                      {retainageChecked && (
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Retainage Percentage"
                            type="number"
                            size="small"
                            fullWidth
                            slotProps={{ min: 0, max: 100, step: 0.01 }}
                            value={retainagePercentage}
                            onChange={(e) => setRetainagePercentage(e.target.value)}
                          />
                        </Grid>
                      )}
                      {resaleCertIssued && (
                        <>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              label="All-In Tax Rate"
                              type="number"
                              size="small"
                              fullWidth
                              slotProps={{ min: 0, max: 100, step: 0.01 }}
                              value={allInTaxRate}
                              onChange={(e) => setAllInTaxRate(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              label="County"
                              type="text"
                              size="small"
                              fullWidth
                              value={county}
                              onChange={(e) => setCounty(e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              label="Latitude (0 to +90)"
                              type="number"
                              size="small"
                              fullWidth
                              value={latitude}
                              onChange={(e) => setLatitude(e.target.value)}
                              onBlur={handleLatitudeBlur}
                              slotProps={{
                                input: {
                                  min: 0,
                                  max: 90,
                                  step: 0.000001,
                                  inputMode: 'decimal',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              label="Longitude (-180 to 0)"
                              type="number"
                              size="small"
                              fullWidth
                              value={longitude}
                              onChange={(e) => setLongitude(e.target.value)}
                              onBlur={handleLongitudeBlur}
                              slotProps={{
                                input: {
                                  min: -180,
                                  max: 0,
                                  step: 0.000001,
                                  inputMode: 'decimal',
                                },
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}

                  {/* Vendor Selection */}
                  <Grid item xs={12}>
                    <Autocomplete
                      filterOptions={normalizedFilterOptions}
                      options={vendors.filter((v) => v.nsVendorId != null)}
                      getOptionLabel={(option) => option.companyName}
                      value={selectedVendor || null}
                      onChange={(_, newValue) => {
                        const fakeEvent = { target: { value: newValue?.nsVendorId } };
                        handleVendorSelect(fakeEvent); // reuse your existing handler
                      }}
                      isOptionEqualToValue={(option, value) => option.nsVendorId === value.nsVendorId}
                      disabled={vendorsLoading || confirmVendorChangeOpen}
                      renderInput={(params) => (
                        <TextField {...params} label="Vendor" size="small" fullWidth variant="outlined" />
                      )}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.nsVendorId}
                          style={{ backgroundColor: option.ssqColorCode || 'transparent' }}
                        >
                          {option.companyName}
                        </li>
                      )}
                    />

                    {selectedVendor && (
                      <List dense>
                        <ListItem sx={{ padding: '2px 4px' }}>
                          <ListItemText
                            primary={`Selected Vendor Name: ${selectedVendor.companyName}`}
                            secondary={`Vendor ID: ${selectedVendor.nsVendorId}`}
                            sx={{
                              backgroundColor: selectedVendor.ssqColorCode || 'transparent',
                              padding: '6px',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                            }}
                          />
                        </ListItem>
                      </List>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {/* Right Column (6) - Dates & Vendor Selection */}
              <Grid item xs={6}>
                <Grid container spacing={1}>
                  {/* Proposal Due Date & Desired Delivery Date */}
                  <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Proposal Due Date"
                        value={localRequest.proposalDueDate ? new Date(localRequest.proposalDueDate) : new Date()}
                        minDate={!rfqId ? new Date() : null}
                        onChange={(date) => handleLocalDateChange('proposalDueDate', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            variant: 'outlined',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Desired Delivery / Service Completion Date"
                        value={localRequest.desiredDeliveryDate ? new Date(localRequest.desiredDeliveryDate) : null}
                        minDate={localRequest.proposalDueDate ? new Date(localRequest.proposalDueDate) : null}
                        onChange={(date) => handleLocalDateChange('desiredDeliveryDate', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            variant: 'outlined',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Delivery Location"
                      fullWidth
                      size="small"
                      name="deliveryLocation"
                      value={localRequest.deliveryLocation}
                      onChange={handleLocalInputChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      filterOptions={normalizedFilterOptions}
                      size="small"
                      options={projects}
                      getOptionLabel={(option) => `${option.name} (AFE: ${option.afeNumber})`}
                      value={projects.find((p) => p.projectId === selectedProjectIdForSubsidiary) || null}
                      onChange={(_, newValue) => {
                        if (newValue) handleProjectSelect({ target: { value: newValue.projectId } });
                      }}
                      isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
                      renderOption={(props, option) => (
                        <li {...props} key={option.projectId}>
                          {option.name} (AFE: {option.afeNumber})
                        </li>
                      )}
                      disabled={projectsLoading}
                      renderInput={(params) => (
                        <TextField {...params} label="Additional Projects (If Required)" fullWidth variant="outlined" />
                      )}
                    />

                    {/* Subsidiary Dropdown - Shown after project is selected */}
                    {selectedProjectIdForSubsidiary && (
                      <Autocomplete
                        size="small"
                        options={subsidiaryOptions.filter(
                          (s) =>
                            !selectedProjectSubsidiaryPairs.some(
                              (pair) =>
                                String(pair.projectId) === String(selectedProjectIdForSubsidiary) &&
                                String(pair.subsidiaryId) === String(s.id)
                            )
                        )}
                        getOptionLabel={(option) => option.refName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => {
                          if (newValue) handleSubsidiarySelect(newValue.id);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Linked Subsidiary"
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 1 }}
                          />
                        )}
                        noOptionsText="No Remaining Subsidiaries For Selection"
                      />
                    )}

                    {/* Display current project + subsidiary pairs */}
                    <List dense sx={{ mt: 1 }}>
                      {(() => {
                        const basePair = selectedProjectSubsidiaryPairs.find(
                          (pair) =>
                            String(pair.projectId) === String(baseProjectId) &&
                            String(pair.subsidiaryId) === String(baseSubsidiaryId)
                        );

                        const otherPairs = selectedProjectSubsidiaryPairs.filter(
                          (pair) =>
                            !(
                              String(pair.projectId) === String(baseProjectId) &&
                              String(pair.subsidiaryId) === String(baseSubsidiaryId)
                            )
                        );

                        const orderedPairs = basePair ? [basePair, ...otherPairs] : otherPairs;

                        return orderedPairs.map((pair) => {
                          const proj = projects.find((p) => p.projectId === pair.projectId);
                          const isBasePair =
                            String(pair.projectId) === String(baseProjectId) &&
                            String(pair.subsidiaryId) === String(baseSubsidiaryId);

                          return (
                            <ListItem
                              key={`${pair.projectId}-${pair.subsidiaryId}`}
                              sx={{ padding: '2px 4px' }}
                              secondaryAction={
                                isBasePair ? (
                                  <IconButton edge="end" size="small" disabled>
                                    <DeleteIcon fontSize="small" sx={{ color: 'grey.400' }} />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    size="small"
                                    onClick={() => handleRemoveProject(pair.projectId, pair.subsidiaryId)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )
                              }
                            >
                              <ListItemText
                                primary={proj?.name}
                                secondary={`AFE: ${proj?.afeNumber} | Budget ID: ${pair.budgetId} | Subsidiary ID: ${pair.subsidiaryId}`}
                                sx={{ fontSize: '0.875rem' }}
                              />
                            </ListItem>
                          );
                        });
                      })()}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* Right Column (3) - Attachments */}
          <Grid item xs={3}>
            <Box
              onDrop={(e) => {
                if (isSaving) return;
                e.preventDefault();
                const droppedFiles = Array.from(e.dataTransfer.files);
                setNewAttachments((prev) => [...prev, ...droppedFiles]);
              }}
              onDragOver={(e) => {
                if (!isSaving) e.preventDefault();
              }}
            >
              <Paper variant="outlined" sx={{ padding: '12px', height: '100%' }}>
                <Typography variant="h6">Attachments</Typography>
                <Button
                  variant="contained"
                  component="label"
                  fullWidth
                  size="small"
                  disabled={!isLoaded || isSaving}
                  startIcon={<AttachFileIcon />}
                >
                  Click Here To Upload Files
                  <input type="file" hidden multiple onChange={handleFileUpload} />
                </Button>
                <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                  <strong>(Or Drag and Drop Files Here - If Button Is Enabled)</strong>
                </Typography>
                {(existingAttachments.length > 0 || newAttachments.length > 0) && (
                  <List dense sx={{ mt: 1 }}>
                    {[...existingAttachments, ...newAttachments].map((file, index) => {
                      const isExisting = !!file.blobName;
                      const fileName = isExisting ? file.fileName : file.name;
                      const fileSize = isExisting
                        ? `${((file.fileSize || 0) / 1024).toFixed(2)} KB`
                        : `${((file.size || 0) / 1024).toFixed(2)} KB`;
                      const downloadUrl = isExisting
                        ? `/api/RfqHeader/GetBlobFileContent?blobItemPath=${encodeURIComponent(file.blobName)}`
                        : null;

                      return (
                        <ListItem
                          key={index}
                          sx={{ padding: '2px 4px' }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveAttachment(file)}
                              disabled={isSaving}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              isExisting && downloadUrl ? (
                                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                                  {fileName}
                                </a>
                              ) : (
                                fileName
                              )
                            }
                            secondary={fileSize}
                            sx={{ fontSize: '0.875rem' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Box>
            <Box mt={2}>
              <TextField
                label="Special Instructions to Vendor"
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                name="vendorInstructions"
                value={localRequest.vendorInstructions}
                onChange={handleLocalInputChange}
                variant="outlined"
              />
            </Box>
          </Grid>
        </Grid>

        {/* AG Grid Section */}
        <Grid container spacing={1.5} sx={{ marginTop: '16px' }}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  type="number"
                  label="Lines To Add"
                  variant="outlined"
                  size="small"
                  value={rowsToAdd}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setRowsToAdd(isNaN(val) ? 1 : Math.min(100, Math.max(1, val)));
                  }}
                  inputProps={{
                    min: 1,
                    max: 100,
                    style: { padding: '8.5px 10px', width: '80px', textAlign: 'center' },
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddRow}
                  disabled={!isLoaded || isSaving}
                >
                  Add Line Item(s)
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => setExpandGridOpen(true)}
                  disabled={!isLoaded || isSaving}
                >
                  Expand Grid
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={() => {
                    const warningNote = [
                      [
                        'NOTE: Do NOT add rows inbetween here and the table below. Only valid projects + subsidiary pairs, material categories, UOMs, and tax rates  from the helper sheets will import correctly.',
                      ],
                      [],
                    ];

                    const coreSheetData = [
                      [
                        'Project',
                        'Subsidiary',
                        'Material Category',
                        'UOM',
                        'Quantity',
                        'Unit Price',
                        'Tax Rate',
                        'Description',
                        'Vendor Comments',
                        'Manufacturer',
                        'Model',
                        'Lead Time',
                      ],
                    ];

                    const lineItemSheetData = [...warningNote, ...coreSheetData];

                    const projectSheet = [
                      ['Project Name', 'Subsidiary Name'],
                      ...selectedProjectSubsidiaryPairs
                        .map((pair) => {
                          const proj = projects.find((p) => p.projectId === pair.projectId);
                          return [proj?.name || 'UNKNOWN PROJECT', pair.subsidiaryName || 'UNKNOWN SUBSIDIARY'];
                        })
                        .sort((a, b) => {
                          const projectCompare = a[0].localeCompare(b[0]);
                          if (projectCompare !== 0) return projectCompare;
                          return a[1].localeCompare(b[1]);
                        }),
                    ];

                    const materialCategorySheet = [['Material Category'], ...accounts.map((acct) => [acct.fullname])];
                    const uomSheet = [['UOM'], ...uoms.map((u) => [u.name])];
                    const taxCodesSheet = [
                      ['Tax Rate Label'],
                      ...taxCodes
                        .filter((tc) => tc?.text) // Omit undefined/null
                        .map((tc) => [tc.text]),
                    ];

                    const wb = XLSX.utils.book_new();
                    const lineItemSheet = XLSX.utils.aoa_to_sheet(lineItemSheetData);

                    // Merge A1:E1 for the note
                    lineItemSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];

                    // Set column widths
                    lineItemSheet['!cols'] = [
                      { wch: 50 }, // Project
                      { wch: 50 }, // Subsidiary
                      { wch: 50 }, // Material Category
                      { wch: 20 }, // UOM
                      { wch: 20 }, // Quantity
                      { wch: 20 }, // Unit Price
                      { wch: 20 }, // Tax Rate
                      { wch: 50 }, // Description
                      { wch: 50 }, // Vendor Comments
                      { wch: 25 }, // Manufacturer
                      { wch: 25 }, // Model
                      { wch: 20 }, // Lead Time
                    ];

                    const projectsSheet = XLSX.utils.aoa_to_sheet(projectSheet);
                    projectsSheet['!cols'] = [{ wch: 100 }, { wch: 100 }];

                    const categoriesSheet = XLSX.utils.aoa_to_sheet(materialCategorySheet);
                    categoriesSheet['!cols'] = [{ wch: 100 }];

                    const uomsSheet = XLSX.utils.aoa_to_sheet(uomSheet);
                    uomsSheet['!cols'] = [{ wch: 100 }];

                    const taxSheet = XLSX.utils.aoa_to_sheet(taxCodesSheet);
                    taxSheet['!cols'] = [{ wch: 100 }];

                    XLSX.utils.book_append_sheet(wb, lineItemSheet, 'Line Items');
                    XLSX.utils.book_append_sheet(wb, projectsSheet, 'Valid Projects + Subsidiary');
                    XLSX.utils.book_append_sheet(wb, categoriesSheet, 'Valid Material Categories');
                    XLSX.utils.book_append_sheet(wb, uomsSheet, 'Valid UOMs');
                    XLSX.utils.book_append_sheet(wb, taxSheet, 'Valid Tax Rates');

                    XLSX.writeFile(wb, 'PO_LineItems_Template.xlsx');
                  }}
                  disabled={!isLoaded || isSaving}
                >
                  Export Excel Template
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  component="label"
                  size="small"
                  disabled={!isLoaded || isSaving}
                >
                  Import From Template
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (rowData.length > 0) {
                          setImportFile(file);
                          setImportDialogOpen(true);
                        } else {
                          handleImportExcel(file);
                        }
                      }
                      e.target.value = null; // reset input
                    }}
                  />
                </Button>
              </Box>
              <Typography variant="h6">
                Awarded Ext Price + Tax: $
                {totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
            <div style={{ height: '350px', width: '100%' }}>
              <AgGridReact
                ref={mainGridApiRef}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowData={rowData}
                cellSelection={true}
                copyHeadersToClipboard={false}
                enterNavigatesVertically={true}
                enterNavigatesVerticallyAfterEdit={true}
                stopEditingWhenCellsLoseFocus={true}
                suppressScrollOnNewData={true}
                rowSelection={{
                  mode: 'multiRow',
                  checkboxes: false,
                  headerCheckbox: false,
                  suppressRowClickSelection: true,
                }}
                onGridReady={(params) => {
                  mainGridApiRef.current = params.api;
                }}
                onCellValueChanged={(params) => {
                  const field = params.colDef.field;

                  if (field === 'selectedProject') {
                    const match = selectedProjectSubsidiaryPairs.find((pair) => {
                      const proj = projects.find((p) => p.projectId === pair.projectId);
                      return proj?.name === params.newValue;
                    });
                    if (match) {
                      const selectedProj = projects.find((p) => p.projectId === match.projectId);
                      params.data.projectId = match.projectId;
                      params.data.netsuiteBudgetId = match.budgetId;
                      params.data.selectedProject = selectedProj?.name || '';
                    }
                  }

                  if (field === 'selectedSubsidiary') {
                    const match = selectedProjectSubsidiaryPairs.find(
                      (pair) => pair.subsidiaryName === params.newValue
                    );
                    if (match) {
                      params.data.lineItemSubsidiaryId = match.subsidiaryId;
                    }
                  }

                  // handle picking a new tax rate
                  if (field === 'taxCode') {
                    const selected = taxCodes.find((tc) => tc.value === params.newValue);
                    params.data.taxRate = selected?.rate ?? 0;
                    params.data.taxCode = selected?.value ?? null;
                    // re-calc ext price
                    params.data.totalPrice = (params.data.quantity || 0) * (params.data.unitPrice || 0);
                    params.api.refreshCells({
                      rowNodes: [params.node],
                      columns: ['totalPrice', 'taxAmount', 'awardedPlusTax'],
                      force: true,
                    });
                  }

                  // handle manual edits to quantity or unitPrice
                  if (['quantity', 'unitPrice'].includes(field)) {
                    params.data.totalPrice = (params.data.quantity || 0) * (params.data.unitPrice || 0);
                    const selectedTax = taxCodes.find((tc) => tc.value === params.data.taxCode);
                    params.data.taxRate = selectedTax?.rate ?? 0;
                    params.api.refreshCells({
                      rowNodes: [params.node],
                      columns: ['totalPrice', 'taxAmount', 'awardedPlusTax'],
                      force: true,
                    });
                  }

                  // finally, pull the *entire* grid back into React state
                  const updatedRows = [];
                  params.api.forEachNode((node) => {
                    // ensure awardedPlusTax is up to date
                    node.data.awardedPlusTax = (node.data.totalPrice || 0) * (1 + (node.data.taxRate || 0));
                    updatedRows.push(node.data);
                  });
                  setRowData(updatedRows);
                }}
                getContextMenuItems={(params) => {
                  const cellRanges = params.api.getCellRanges() || [];

                  const selectedRows = new Set();

                  for (const range of cellRanges) {
                    const startRowIndex = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
                    const endRowIndex = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

                    for (let i = startRowIndex; i <= endRowIndex; i++) {
                      const rowNode = params.api.getDisplayedRowAtIndex(i);
                      if (rowNode?.data) {
                        selectedRows.add(rowNode.data);
                      }
                    }
                  }

                  const rowsToDelete = Array.from(selectedRows);

                  return [
                    'copy',
                    'paste',
                    'separator',
                    {
                      name: `Delete ${rowsToDelete.length} Row(s)`,
                      icon: '<i class="ag-icon ag-icon-trash"></i>',
                      disabled: rowsToDelete.length === 0 || isSaving,
                      action: (params) => {
                        params.api.applyTransaction({ remove: rowsToDelete });

                        setRowData((prev) => {
                          const updated = prev.filter((r) => !rowsToDelete.includes(r));
                          return updated.map((item, i) => ({
                            ...item,
                            lineItemNumber: i + 1,
                          }));
                        });

                        rowsToDelete.forEach((r) => {
                          if (r.id) {
                            setDeletedRowIds((prev) => [...prev, r.id]);
                          }
                        });
                      },
                    },
                  ];
                }}
              />
            </div>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: '12px 24px' }}>
        {reviewMode ? (
          <>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={() => setDenyReasonOpen(true)}
              disabled={!isLoaded || isSaving}
            >
              Deny
            </Button>
            <Button
              color="success"
              variant="contained"
              size="small"
              disabled={!isLoaded || isSaving}
              onClick={async () => {
                try {
                  setIsSaving(true);
                  const savedRfq = await handleSaveProgress();
                  if (onApproveRfq) {
                    await onApproveRfq(savedRfq);
                  }
                } catch (err) {
                  toast.error('Failed to save and prepare for approval.');
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              {isSaving ? <CircularProgress size={16} /> : 'Approve'}
            </Button>
          </>
        ) : (
          <>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={() => setSaveConfirmDialogOpen(true)}
              disabled={!isLoaded || isSaving}
            >
              {isSaving ? <CircularProgress size={16} /> : 'Save Progress / Exit'}
            </Button>
            <Button
              color="warning"
              variant="contained"
              size="small"
              onClick={() => setSubmitConfirmDialogOpen(true)}
              disabled={!isLoaded || isSaving}
            >
              Submit For Approval
            </Button>
          </>
        )}
      </DialogActions>

      <ConfirmDialog
        open={confirmVendorChangeOpen}
        message="Changing vendors will delete all current attachments. Do you want to continue?"
        loading={false}
        onConfirm={handleVendorChangeProceed}
        onCancel={handleVendorChangeCancel}
        onClose={() => {}}
        showNo={true}
        showCancel={false}
        confirmLabel="Yes"
        noLabel="No"
      />

      <ConfirmDialog
        open={saveConfirmDialogOpen}
        message="Choose a save option."
        loading={isSaving}
        // “Save and Continue”
        confirmLabel={isSaving ? <CircularProgress size={16} /> : 'Save and Continue'}
        onConfirm={async () => {
          await handleSaveProgress();
          setSaveConfirmDialogOpen(false);
        }}
        // “Save and Exit”
        showNo={true}
        noLabel="Save and Exit"
        onCancel={handleSaveAndExit}
        // “Exit without Saving”
        showCancel={true}
        cancelLabel="Exit without Saving"
        onClose={async () => {
          setSaveConfirmDialogOpen(false);
          if (onUpdateRfq) await onUpdateRfq();
          if (onClose) onClose();
        }}
      />

      <ConfirmDialog
        open={saveConfirmDialogOpen}
        message="Choose a save option."
        loading={isSaving}
        onConfirm={async () => {
          await handleSaveProgress();
          setSaveConfirmDialogOpen(false);
        }}
        onCancel={handleSaveAndExit}
        onClose={null}
        cancelLabel="Exit without Saving"
        onCancelClick={() => {
          setSaveConfirmDialogOpen(false); // Exit without Saving
          if (onClose) onClose();
        }}
        showNo={true}
        showCancel={true}
        confirmLabel={isSaving ? <CircularProgress size={16} /> : 'Save and Continue'}
        noLabel="Save and Exit"
      />

      <ConfirmDialog
        open={submitConfirmDialogOpen}
        message="Are you sure you want to submit this request for approval?"
        loading={isSaving}
        onConfirm={handleSubmitForApproval}
        onCancel={handleSaveProgress}
        onClose={() => setSubmitConfirmDialogOpen(false)}
        showNo={true}
        showCancel={true}
        confirmLabel="Yes"
        noLabel="No, but save progress"
        cancelLabel="Cancel"
      />

      <ConfirmDialog
        open={confirmExitWithoutSavingOpen}
        loading={false}
        onConfirm={() => {
          setConfirmExitWithoutSavingOpen(false);
          setSaveConfirmDialogOpen(false);
          setTimeout(() => {
            if (onClose) onClose();
          }, 0);
        }}
        onCancel={() => {
          setConfirmExitWithoutSavingOpen(false);
          setSaveConfirmDialogOpen(false);
        }}
        onClose={() => setConfirmExitWithoutSavingOpen(false)}
        showNo={true}
        showCancel={false}
        confirmLabel="Yes"
        noLabel="No"
        message={
          <Box>
            <Typography variant="body1">Required header information has not been entered.</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Progress will not be saved. Continue without saving?
            </Typography>
          </Box>
        }
      />

      <ConfirmDialog
        open={importDialogOpen}
        message="Importing from Excel will append the line items to the table. Continue?"
        confirmLabel="Append"
        cancelLabel="Cancel"
        showCancel={true}
        showNo={false}
        onClose={() => setImportDialogOpen(false)}
        onConfirm={() => {
          if (importFile) {
            handleImportExcel(importFile);
          }
          setImportDialogOpen(false);
          setImportFile(null);
        }}
        onCancelClick={() => {
          setImportDialogOpen(false);
          setImportFile(null);
        }}
      />

      <PoGridExpansionModal
        open={expandGridOpen}
        onClose={() => {
          setExpandGridOpen(false);
          setTimeout(() => {
            if (mainGridApiRef.current) {
              mainGridApiRef.current.stopEditing();
              mainGridApiRef.current.refreshCells({ force: true });
            }
          }, 0);
        }}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={rowData}
        setRowData={setRowData}
        rowsToAdd={rowsToAdd}
        setRowsToAdd={setRowsToAdd}
        handleAddRow={handleAddRow}
        taxCodes={taxCodes}
        selectedProjectSubsidiaryPairs={selectedProjectSubsidiaryPairs}
        projects={projects}
        accounts={accounts}
        uoms={uoms}
        onImportExcel={handleImportExcel}
        setDeletedRowIds={setDeletedRowIds}
      />
      <DenyReasonDialog open={denyReasonOpen} onClose={() => setDenyReasonOpen(false)} onSubmit={handleDenyWithNote} />
    </Dialog>
  );
};

export default PoDataEntryModal;
